import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { uuidv7 } from 'uuidv7';
import { DateTime } from 'luxon';
import { z } from 'zod';
import { AutoAssignType } from '../api/bucket-types';
import { useBudgetId } from '../../budget/budget-store';
import { useTimeZone } from '../../user/api/use-get-user';
import { useBuckets } from '../api/use-get-buckets';
import { useCreateBucketApi } from '../api/use-create-delete-bucket';
import { useUpdateBucketApi } from '../api/use-update-bucket';
// Bucket Form Schema - uses form-specific enum
export const bucketFormSchema = z.object({
    // Basic bucket properties
    name: z.string().trim().min(1, 'Bucket name is required').max(100, 'Bucket name must be 100 characters or less'),
    notes: z.string().trim().optional().or(z.literal('')),
    categoryId: z.string().uuid().optional(),
    archived: z.boolean().optional(),
    favorite: z.boolean().optional(),
    // Auto-assign properties (using form enum that includes NONE)
    autoAssignType: z.nativeEnum(AutoAssignType).optional(),
    autoAssignAmount: z.coerce.number().optional(),
    autoAssignMonth: z.number().min(1).max(12).optional(),
    autoAssignYear: z
        .number()
        .min(DateTime.now().year - 1)
        .max(DateTime.now().year + 10)
        .optional(),
    autoAssignRecurrenceMonths: z.string().optional(),
    autoAssignTopOff: z.boolean().optional(),
    autoAssignDisabled: z.boolean().optional(),
});
// Bucket Form Hook
export const useBucketForm = ({ bucket, onSuccess, categoryId, defaultAutoAssignType = AutoAssignType.NONE, }) => {
    const budgetId = useBudgetId();
    const timeZone = useTimeZone();
    const buckets = useBuckets();
    const bucketsInCategory = buckets.filter((b) => b.categoryId === categoryId);
    const bucketCount = bucketsInCategory.length;
    // mutations
    const { mutate: createBucket, isPending: isCreatePending } = useCreateBucketApi({ onSuccess });
    const { mutate: updateBucket, isPending: isUpdatePending } = useUpdateBucketApi({ onSuccess });
    // enhance schema with validation rules
    const schema = bucketFormSchema
        .refine((data) => {
        const trimmedName = data.name?.trim() || '';
        const isNameTaken = buckets.some((b) => b.name.trim().toLowerCase() === trimmedName.toLowerCase() && b.id !== bucket?.id);
        return !isNameTaken;
    }, {
        message: 'A bucket (or archived bucket) with this name already exists',
        path: ['name'],
    })
        .superRefine((data, ctx) => {
        // Check if `archived` is false and `categoryId` is missing
        if (data.archived === false && !data.categoryId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Please select a category to restore into',
                path: ['categoryId'],
            });
        }
        // Auto-assign validation (only when type is FIXED or TARGET)
        if (data.autoAssignType && data.autoAssignType !== AutoAssignType.NONE) {
            if (!data.autoAssignAmount || data.autoAssignAmount <= 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Auto-assign amount must be greater than 0',
                    path: ['autoAssignAmount'],
                });
            }
            if (data.autoAssignType === AutoAssignType.TARGET) {
                if (!data.autoAssignMonth) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Target month is required for TARGET type',
                        path: ['autoAssignMonth'],
                    });
                }
                if (!data.autoAssignYear) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Target year is required for TARGET type',
                        path: ['autoAssignYear'],
                    });
                }
            }
        }
    });
    const defaultValues = useMemo(() => toFormData(bucket, categoryId, timeZone, defaultAutoAssignType), [bucket, categoryId, timeZone, defaultAutoAssignType]);
    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues,
    });
    useEffect(() => {
        form.reset(defaultValues);
    }, [bucket, form, defaultValues]);
    /**
     * Handles the form submission event, building the API payload and
     * invoking the correct create or update mutation.
     *
     * @param values - Sanitized bucket form values.
     */
    const submitForm = useCallback((values) => {
        const payload = toRequestData(values, bucket, budgetId, bucketCount, categoryId);
        if (bucket) {
            updateBucket(payload).then(() => { });
        }
        else {
            createBucket(payload).then(() => { });
        }
    }, [bucket, budgetId, bucketCount, categoryId, timeZone, createBucket, updateBucket]);
    return {
        form,
        onSubmit: form.handleSubmit(submitForm),
        isSubmitting: isCreatePending || isUpdatePending,
    };
};
/**
 * Compute the initial values for the BucketForm.
 *
 * @param bucket - Optional existing Bucket to edit
 * @param categoryId - The category ID for new buckets
 * @param timeZone - User's timezone for date calculations
 * @param defaultAutoAssignType - Default auto-assign type for new buckets
 * @returns A fully populated BucketForm
 */
function toFormData(bucket, categoryId, timeZone, defaultAutoAssignType = AutoAssignType.NONE) {
    const currentDate = DateTime.now().setZone(timeZone);
    const autoAssign = bucket?.autoAssign;
    const autoAssignDate = autoAssign?.target ? DateTime.fromISO(autoAssign.target) : currentDate;
    return {
        // Basic bucket properties
        name: bucket?.name?.trim() ?? '',
        notes: bucket?.notes?.trim() ?? '',
        categoryId: bucket?.categoryId ?? categoryId,
        archived: bucket?.archived ?? false,
        favorite: bucket?.favorite ?? false,
        // Auto-assign properties - direct assignment, no conversion needed
        autoAssignType: autoAssign?.type ?? defaultAutoAssignType,
        autoAssignAmount: autoAssign?.amount ?? 0,
        autoAssignMonth: autoAssignDate.month,
        autoAssignYear: autoAssignDate.year,
        autoAssignRecurrenceMonths: autoAssign?.recurrenceMonths?.toString() ?? '0',
        autoAssignTopOff: autoAssign?.topOff ?? false,
        autoAssignDisabled: autoAssign?.disabled ?? false,
    };
}
/**
 * Build the BucketRequest payload from form values and context.
 *
 * @param formValues - The values from the bucket form
 * @param existing - The existing Bucket being edited, or undefined when creating
 * @param budgetId - Current budget identifier
 * @param bucketCount - Number of existing buckets in the category (for default position)
 * @param categoryId - The category ID for new buckets
 * @returns A fully populated BucketRequest for create/update
 */
function toRequestData(formValues, existing, budgetId, bucketCount, categoryId) {
    let autoAssign;
    // Create auto-assign only if type is not NONE
    if (formValues.autoAssignType && formValues.autoAssignType !== AutoAssignType.NONE) {
        autoAssign = {
            type: formValues.autoAssignType,
            amount: formValues.autoAssignAmount,
            target: formValues.autoAssignType === AutoAssignType.TARGET && formValues.autoAssignMonth && formValues.autoAssignYear
                ? DateTime.local(formValues.autoAssignYear, formValues.autoAssignMonth, 1).toISODate()
                : undefined,
            recurrenceMonths: formValues.autoAssignRecurrenceMonths
                ? parseInt(formValues.autoAssignRecurrenceMonths)
                : undefined,
            topOff: Boolean(formValues.autoAssignTopOff),
            disabled: formValues.autoAssignDisabled,
        };
    }
    // If autoAssignType is NONE, autoAssign stays undefined
    return {
        id: existing ? existing.id : uuidv7(),
        budgetId,
        categoryId: formValues.categoryId ?? categoryId,
        name: formValues.name.trim(),
        notes: formValues.notes?.trim() || undefined,
        archived: formValues.archived ?? false,
        favorite: formValues.favorite ?? false,
        position: existing?.position ?? bucketCount,
        assignments: existing?.assignments ?? [],
        autoAssign,
    };
}
